import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AuthModal from "@/components/AuthModal";
import WalletOnboardingModal from "@/components/WalletOnboardingModal";
import { useUser } from "@/context/UserContext";

const X_ERROR_MESSAGES = {
  access_denied: "X authorization was cancelled. You can try again any time.",
  invalid_state: "This sign-in request is invalid. Please try again.",
  expired_state: "This sign-in request expired. Please try again.",
  reused_state: "This sign-in link was already used. Please try again.",
  invalid_request: "The X response was incomplete. Please try again.",
  token_failed: "X did not complete the sign-in. Please try again.",
  user_failed: "Could not confirm your X identity. Please try again.",
  already_linked: "That X account is already linked to another Osteon account.",
  x_account_already_linked: "This account already has an X identity linked. Unlinking is not available here.",
  session_mismatch: "Your session changed during sign-in. Please try again.",
  session_expired: "Your session expired during sign-in. Please try again.",
  user_missing: "Your Osteon account was not found. Please sign in again.",
  account_banned: "This account is not available.",
};

function XReturnHandler() {
  const { refresh } = useUser();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("auth");
    if (!status || !status.startsWith("x_")) return;
    // Remove OAuth status params immediately so refresh/re-share is clean.
    // Tokens never travel in the URL: only a coarse status + reason.
    const clean = new URL(window.location.href);
    clean.searchParams.delete("auth");
    clean.searchParams.delete("reason");
    window.history.replaceState({}, "", clean.toString());

    if (status === "x_success") {
      refresh()
        .then(() => toast.success("Signed in with X"))
        .catch(() => toast.error("Signed in, but the profile did not load. Reload to retry."));
    } else if (status === "x_linked") {
      refresh()
        .then(() => toast.success("X account linked"))
        .catch(() => toast.error("Linked, but the profile did not load. Reload to retry."));
    } else if (status === "x_error") {
      const reason = params.get("reason");
      toast.error(X_ERROR_MESSAGES[reason] || "X sign-in failed. Please try again.");
      // A failed login may still have a valid prior session; re-sync it.
      refresh().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

const SKIP_WALLET_KEY = "osteon_skip_wallet";

function WalletOnboardingGate() {
  const { user } = useUser();
  const [dismissed, setDismissed] = useState(
    () => window.sessionStorage.getItem(SKIP_WALLET_KEY) === "1"
  );

  // Post-login wallet step: X-bound account with no wallet yet, and not
  // skipped during this browser session.
  const show = !!user && !user.wallet_address && !dismissed;

  const skip = () => {
    window.sessionStorage.setItem(SKIP_WALLET_KEY, "1");
    setDismissed(true);
  };

  return <WalletOnboardingModal open={show} onClose={skip} />;
}

export default function PublicLayout() {
  const [authOpen, setAuthOpen] = useState(false);
  return (
    <div className="relative min-h-screen bg-[#07080c] text-slate-100 overflow-x-hidden">
      <div className="pointer-events-none fixed inset-0 grid-bg opacity-60" />
      <Navbar onOpenAuth={() => setAuthOpen(true)} />
      <main className="relative">
        <Outlet context={{ openAuth: () => setAuthOpen(true) }} />
      </main>
      <Footer />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialRef={new URLSearchParams(window.location.search).get("ref")} />
      <XReturnHandler />
      <WalletOnboardingGate />
    </div>
  );
}
